import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";

export function UsersPage() {
  const { org, may, viewOnly } = useAgents();

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Users and roles</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Humans sign in with ATW auth. Agents are workers, not logins. Every Workforce user needs
            at least the Standard role.
          </p>
        </div>
        <Button
          type="button"
          disabled={!may("manage_users")}
          onClick={() => toast.success("Invite user is a Phase 1 placeholder.")}
        >
          Invite user
        </Button>
      </div>

      {viewOnly ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Super Admin manages users and custom roles.
        </p>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Roles</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {org.roles.map((role) => (
            <Card key={role.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  {role.name}
                  {role.builtIn ? <Badge variant="secondary">Built-in</Badge> : null}
                  {role.viewOnlyDefault ? <Badge variant="outline">View only</Badge> : null}
                </CardTitle>
                <CardDescription>{role.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-inside list-disc text-xs text-muted-foreground">
                  {role.permissions.map((p) => (
                    <li key={p}>{p.replace(/_/g, " ")}</li>
                  ))}
                </ul>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  disabled={!may("manage_roles") || role.id === "super_admin"}
                  onClick={() =>
                    toast.success(
                      role.id === "standard"
                        ? "Standard permission rules are editable later by Super Admin."
                        : "Custom role editor placeholder.",
                    )
                  }
                >
                  Edit rules
                </Button>
              </CardContent>
            </Card>
          ))}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-base">Custom role</CardTitle>
              <CardDescription>
                Super Admin creates roles with permission rules (not a fixed Admin/Viewer ladder).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                size="sm"
                disabled={!may("manage_roles")}
                onClick={() => toast.success("Create custom role is a placeholder.")}
              >
                Create role
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Users</h2>
        <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-[#1D232D]">
          {org.users.map((user) => (
            <li
              key={user.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {user.roles.map((r) => (
                  <Badge key={r} variant="outline">
                    {r === "super_admin" ? "Super Admin" : r === "standard" ? "Standard" : r}
                  </Badge>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
